/**
 * Human labels + soft-chip colors for the four medication status dimensions
 * and provenance. Shared across the design proposals so status language and
 * color stay consistent (and reviewable) between them.
 */

import type { ChipColor } from '@jotpsych/ui';

import type {
  AllergySeverity,
  AttentionCategory,
  BenefitLevel,
  EpisodeStatus,
  MedicationSource,
  MonitoringStatus,
  ResponseLevel,
  TakingStatus,
  VerificationStatus,
} from './vm/types';

export const episodeStatusLabel: Record<EpisodeStatus, string> = {
  current: 'Current',
  held: 'On hold',
  stopped: 'Stopped',
  completed: 'Completed',
  historical: 'Historical',
  uncertain: 'Uncertain',
  suggested: 'Suggested',
};

export const episodeStatusColor: Record<EpisodeStatus, ChipColor> = {
  current: 'success',
  held: 'warning',
  stopped: 'error',
  completed: 'default',
  historical: 'default',
  uncertain: 'info',
  suggested: 'info',
};

export const takingStatusLabel: Record<TakingStatus, string> = {
  taking_as_prescribed: 'Taking as prescribed',
  taking_differently: 'Taking differently',
  temporarily_held: 'Temporarily held',
  not_currently_taking: 'Not currently taking',
  unknown: 'Unknown',
  unable_to_verify: 'Unable to verify',
};

export const takingStatusColor: Record<TakingStatus, ChipColor> = {
  taking_as_prescribed: 'success',
  taking_differently: 'warning',
  temporarily_held: 'warning',
  not_currently_taking: 'error',
  unknown: 'default',
  unable_to_verify: 'default',
};

export const verificationStatusLabel: Record<VerificationStatus, string> = {
  unreviewed: 'Unreviewed',
  patient_confirmed: 'Patient confirmed',
  caregiver_confirmed: 'Caregiver confirmed',
  clinician_confirmed: 'Clinician confirmed',
  conflicting_sources: 'Conflicting sources',
  unable_to_verify: 'Unable to verify',
};

export const verificationStatusColor: Record<VerificationStatus, ChipColor> = {
  unreviewed: 'default',
  patient_confirmed: 'info',
  caregiver_confirmed: 'info',
  clinician_confirmed: 'primary',
  conflicting_sources: 'error',
  unable_to_verify: 'default',
};

export const sourceLabel: Record<MedicationSource, string> = {
  dosespot_order: 'DoseSpot',
  surescripts_history: 'Pharmacy',
  pharmacy_history: 'Pharmacy',
  patient_report: 'Patient',
  caregiver_report: 'Caregiver',
  clinician_manual: 'Manual',
  outside_prescriber: 'Outside',
  transition_import: 'Imported',
  administration: 'Administered',
};

export const benefitLabel: Record<BenefitLevel, string> = {
  none: 'No benefit',
  limited: 'Limited benefit',
  partial: 'Partial benefit',
  good: 'Good benefit',
  patient_unsure: 'Patient unsure',
  unknown: 'Benefit unknown',
};

export const responseLabel: Record<ResponseLevel, string> = {
  none: 'No response',
  limited: 'Limited response',
  partial: 'Partial response',
  good: 'Good response',
  remission: 'Remission',
  unknown: 'Response unknown',
};

export const monitoringStatusColor: Record<MonitoringStatus, ChipColor> = {
  due: 'warning',
  overdue: 'error',
  completed: 'success',
  declined: 'default',
  deferred: 'default',
  not_applicable: 'default',
};

export const attentionCategoryLabel: Record<AttentionCategory, string> = {
  clinical_review: 'Clinical review',
  monitoring: 'Monitoring',
  prescription_ops: 'Prescription',
  data_quality: 'Data quality',
};

export const severityColor: Record<'high' | 'medium' | 'low', ChipColor> = {
  high: 'error',
  medium: 'warning',
  low: 'default',
};

export const allergySeverityLabel: Record<AllergySeverity, string> = {
  mild: 'Mild',
  moderate: 'Moderate',
  severe: 'Severe',
  'life-threatening': 'Life-threatening',
};

export const allergySeverityColor: Record<AllergySeverity, ChipColor> = {
  mild: 'default',
  moderate: 'warning',
  severe: 'error',
  'life-threatening': 'error',
};
